import { tsRestFetchApi, type ApiFetcher } from '@ts-rest/core';

// CloudFront OAC for Lambda Function URLs does not sign request bodies for
// POST/PUT/PATCH; the client must precompute SHA-256(body) and send it as
// `x-amz-content-sha256`. OAC includes that header value in the SigV4
// signature it sends to the Function URL, which recomputes the body hash
// and rejects mismatches with InvalidSignatureException. Without this
// header, all mutating requests through CloudFront fail at the edge.
//
// https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-restricting-access-to-lambda.html

const EMPTY_BODY_HASH = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';

const needsBodyHash = (method: string | undefined): boolean => {
  const m = (method ?? 'GET').toUpperCase();
  return m === 'POST' || m === 'PUT' || m === 'PATCH';
};

const toHex = (bytes: Uint8Array): string => {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
};

const sha256Hex = async (bytes: Uint8Array): Promise<string> => {
  const hash = await crypto.subtle.digest('SHA-256', bytes.buffer as ArrayBuffer);
  return toHex(new Uint8Array(hash));
};

// FormData → serialized multipart bytes. Critical: the boundary in the
// returned Content-Type matches the bytes, so the hash we compute matches
// what the server actually receives. Passing raw FormData to fetch would
// let it regenerate a different boundary.
const serializeFormData = async (fd: FormData): Promise<{ body: Uint8Array; contentType: string }> => {
  const req = new Request('http://localhost/', { method: 'POST', body: fd });
  const buf = await req.arrayBuffer();
  const ct = req.headers.get('Content-Type') ?? 'multipart/form-data';
  return { body: new Uint8Array(buf), contentType: ct };
};

/**
 * Resolves a body value to bytes + hash + optional Content-Type override.
 * Returns null for "no hash needed" (caller should use the original body).
 */
const hashBody = async (
  body: BodyInit | null | undefined
): Promise<{ hash: string; body: BodyInit | null | undefined; contentType?: string } | null> => {
  if (body == null || body === '') return { hash: EMPTY_BODY_HASH, body };
  if (typeof body === 'string') {
    return { hash: await sha256Hex(new TextEncoder().encode(body)), body };
  }
  if (body instanceof FormData) {
    const { body: bytes, contentType } = await serializeFormData(body);
    return { hash: await sha256Hex(bytes), body: bytes as BodyInit, contentType };
  }
  if (body instanceof URLSearchParams) {
    return { hash: await sha256Hex(new TextEncoder().encode(body.toString())), body };
  }
  if (body instanceof Blob) {
    const buf = new Uint8Array(await body.arrayBuffer());
    return { hash: await sha256Hex(buf), body };
  }
  if (body instanceof ArrayBuffer) {
    return { hash: await sha256Hex(new Uint8Array(body)), body };
  }
  if (ArrayBuffer.isView(body)) {
    return { hash: await sha256Hex(new Uint8Array(body.buffer, body.byteOffset, body.byteLength)), body };
  }
  return null;
};

/**
 * Drop-in `fetch` replacement that adds `x-amz-content-sha256` on
 * POST/PUT/PATCH. Use for any direct fetch call to the API.
 */
export const signedFetch: typeof fetch = async (input, init) => {
  if (!needsBodyHash(init?.method)) return fetch(input, init);

  const result = await hashBody(init?.body);
  if (!result) return fetch(input, init);

  const headers = new Headers(init?.headers);
  headers.set('x-amz-content-sha256', result.hash);
  if (result.contentType) headers.set('Content-Type', result.contentType);

  return fetch(input, { ...init, headers, body: result.body });
};

/**
 * Computes the `x-amz-content-sha256` value for a known body, for cases
 * where the call site can't use `signedFetch` directly (e.g. the
 * `fetchEventSource` SSE client, which has its own fetch under the hood).
 */
export const computeXAmzContentSha256 = async (body: BodyInit | null | undefined): Promise<string> => {
  const result = await hashBody(body);
  return result?.hash ?? EMPTY_BODY_HASH;
};

/**
 * ts-rest `ApiFetcher` that hashes the serialized body before delegating
 * to the default fetcher. Plug this into `initQueryClient` / `initClient`
 * via the `api` option.
 */
export const fetchApiWithSignedBody: ApiFetcher = async (args) => {
  if (!needsBodyHash(args.method)) return tsRestFetchApi(args);

  const result = await hashBody(args.body);
  if (!result) return tsRestFetchApi(args);

  const headers: Record<string, string> = { ...args.headers, 'x-amz-content-sha256': result.hash };
  if (result.contentType) headers['Content-Type'] = result.contentType;

  // ts-rest's body type doesn't include Uint8Array but its default fetcher
  // forwards the value straight to fetch(), which accepts it.
  return tsRestFetchApi({ ...args, headers, body: result.body as typeof args.body });
};
